import { Skeleton } from "@tql/ui-components-react/tql/indicators";

import { useShipmentDetails } from "~/features/loads/hooks/use-shipment-details.ts";
import type { Load } from "~/shared/schemas/loads-schema.ts";

import type { AdditionalDetails } from "../../flyout-details.ts";
import styles from "./drawer-additional-details-ltl.module.css";

type DrawerAdditionalDetailsLtlProps = {
	readonly additionalDetails: AdditionalDetails | undefined;
	readonly isLoading: boolean;
	readonly load: Load;
	readonly billRate: string | null;
};

function FieldItem({
	label,
	value,
	isLoading,
}: Readonly<{ label: string; value: string; isLoading: boolean }>) {
	return (
		<div className={styles.infoItem}>
			<p className={styles.label}>{label}</p>
			{isLoading ? (
				<span className={styles.loadingState}>
					<Skeleton animation={{ type: "wave" }} />
				</span>
			) : (
				<p className={styles.value}>{value}</p>
			)}
		</div>
	);
}

function getDisplayValue(value: string | number | null | undefined): string {
	if (value === null || value === undefined || value === "") {
		return "-";
	}

	return String(value);
}

// function formatLoadValue(value: number | null | undefined): string {
// 	if (value === null || value === undefined || value === 0) {
// 		return "-";
// 	}

// 	return `$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
// }

function formatAccessorialWithContext(
	accessorial: string,
	context: "Pickup" | "Delivery",
): string {
	const lower = accessorial.toLowerCase();
	if (lower.includes("pickup") || lower.includes("delivery")) {
		return accessorial;
	}
	return `${accessorial}-${context}`;
}

function buildAccessorialsDisplay(
	pickupAccessorials: string[],
	deliveryAccessorials: string[],
	selectedAccessorials: string | null | undefined,
): string {
	const labeled = [
		...pickupAccessorials.map((accessorial) =>
			formatAccessorialWithContext(accessorial, "Pickup"),
		),
		...deliveryAccessorials.map((accessorial) =>
			formatAccessorialWithContext(accessorial, "Delivery"),
		),
	];

	if (selectedAccessorials) {
		labeled.push(selectedAccessorials);
	}

	return labeled.length > 0 ? labeled.join(", ") : "-";
}

function buildSpecialInstructions(
	pickupRemarks: string | null | undefined,
	deliveryRemarks: string | null | undefined,
): string {
	const pickup = pickupRemarks?.trim() ?? "";
	const delivery = deliveryRemarks?.trim() ?? "";

	if (pickup && delivery) {
		return `${pickup}; ${delivery}`;
	}

	return pickup || delivery || "-";
}

export function DrawerAdditionalDetailsLtl({
	additionalDetails,
	isLoading,
	load,
	billRate,
}: Readonly<DrawerAdditionalDetailsLtlProps>) {
	const { data: shipmentDetails, isLoading: isShipmentDetailsLoading } =
		useShipmentDetails(load.poNumber);

	const showEmergencyContact = additionalDetails?.hazmat === true;
	const loading = isLoading || isShipmentDetailsLoading;

	const emergencyContactName = getDisplayValue(
		additionalDetails?.emergencyContactName,
	);
	const emergencyContactPhone = getDisplayValue(
		additionalDetails?.emergencyContactNumber,
	);
	const soNumber = getDisplayValue(
		additionalDetails?.soNumber ?? load.soNumber,
	);
	const customerReference = getDisplayValue(
		additionalDetails?.customerReference ?? load.custRefNum,
	);
	const poNumber = getDisplayValue(
		additionalDetails?.poNumber ?? load.poNumber,
	);
	// const loadValue = formatLoadValue(additionalDetails?.loadValue);
	const hazmat = additionalDetails?.hazmat ? "Yes" : "No";
	const accessorialsDisplay = buildAccessorialsDisplay(
		shipmentDetails?.pickups?.[0]?.accessorials ?? [],
		shipmentDetails?.deliveries?.[0]?.accessorials ?? [],
		additionalDetails?.accessorial,
	);
	const specialInstructions =
		(additionalDetails?.specialInstructions?.trim() || undefined) ??
		buildSpecialInstructions(
			shipmentDetails?.pickups?.[0]?.remarks,
			shipmentDetails?.deliveries?.[0]?.remarks,
		);
	const emergencyContact = showEmergencyContact
		? `${emergencyContactName}, ${emergencyContactPhone}`
		: undefined;

	const hasSoNumber =
		(additionalDetails?.soNumber ?? load.soNumber) != null &&
		(additionalDetails?.soNumber ?? load.soNumber) !== "";

	// const hasLoadValue = additionalDetails?.loadValue != null;
	const hasCustomerReference =
		(additionalDetails?.customerReference ?? load?.custRefNum) != null &&
		(additionalDetails?.customerReference ?? load?.custRefNum) !== "";
	const hasHazmat = additionalDetails?.hazmat != null;
	const hasPONumber =
		additionalDetails?.poNumber != null || load?.poNumber != null;
	const hasAccessorials =
		accessorialsDisplay !== "-" && accessorialsDisplay !== "";
	const hasSpecialInstructions =
		specialInstructions !== "-" && specialInstructions !== "";
	const hasEmergencyContact = showEmergencyContact;
	const hasBillRate = billRate !== null;

	const fields: readonly {
		label: string;
		value: string;
		hasValue: boolean;
	}[] = [
		{ label: "SO Number", value: soNumber, hasValue: hasSoNumber },
		//{ label: "Load Value", value: loadValue, hasValue: hasLoadValue },
		{
			label: "Customer Reference",
			value: customerReference,
			hasValue: hasCustomerReference,
		},
		{ label: "Hazmat", value: hazmat, hasValue: hasHazmat },
		{ label: "PO Number", value: poNumber, hasValue: hasPONumber },
		{
			label: "Special Instructions",
			value: specialInstructions,
			hasValue: hasSpecialInstructions,
		},
		{
			label: "Accessorials",
			value: accessorialsDisplay,
			hasValue: hasAccessorials,
		},
		...(showEmergencyContact
			? [
					{
						label: "Emergency Contact",
						value: emergencyContact ?? "-",
						hasValue: hasEmergencyContact,
					},
				]
			: []),
		...(billRate === null
			? []
			: [
					{
						label: "Bill Rate",
						value: billRate,
						hasValue: hasBillRate,
					},
				]),
	];

	return (
		<div className={styles.wrapper}>
			<div className={styles.infoGrid}>
				{fields.map((field) => (
					<FieldItem
						key={field.label}
						isLoading={loading ? !field.hasValue : false}
						label={field.label}
						value={field.value}
					/>
				))}
			</div>
		</div>
	);
}
